import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { SuiDialogBase } from '@sui/dialog';
import { DialogCloseReason } from '@sui/dialog';
import { ComboboxOption } from '@sui/combobox';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, Observable, combineLatest, map, tap } from 'rxjs';

import { AppState } from 'src/app/reducers';
import {
  selectWidgetPresets,
  selectLoadingWidgetPresets,
  selectWidgtetPresetGroups,
} from 'src/app/core/reducers';
import {
  loadWidgetPresets,
  loadWidgetPresetGroups,
} from 'src/app/core/actions/widget.actions';

import { NomenclatureItem, Widget } from 'src/app/core/models/core.models';
import { WidgetPreset } from 'src/app/core/models/widget.models';
import { WidgetPreviewEvent } from '../../cards/widget-preset-card/widget-preset-card.component';

export interface SwapWidgetDialogInput {
  widget: Widget;
  objectTypeId: number;
  objectClassId: number;
}

export interface SwapWidgetDialogOutput {
  chartId: number;
  chartTypeId: number;
}

@Component({
  selector: 'app-swap-widget-dialog',
  templateUrl: './swap-widget-dialog.component.html',
  styleUrls: ['./swap-widget-dialog.component.scss'],
  standalone: false,
})
export class SwapWidgetDialogComponent
  extends SuiDialogBase<SwapWidgetDialogInput, SwapWidgetDialogOutput>
  implements OnInit
{
  private destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private translateService = inject(TranslateService);

  // The sui-combobox dropdown mounts into the dialog's content pane (resolved by
  // selector at open time) so it shares the dialog's stacking context and renders
  // above it — not behind it at body level. The registry/host approach used by
  // dynamic-form can't work here: this component *owns* sui-dialog-simple, so the
  // boundary pane is a descendant of our host, not an ancestor to walk up to.
  readonly dropdownBoundary = '.sui-dialog-simple__content';

  widgetPresets$: Observable<WidgetPreset[]>;
  widgetPresetsLoading$: Observable<boolean>;
  widgetPresetsFiltered: WidgetPreset[] = [];
  widgetPresets: WidgetPreset[] = [];
  groups: NomenclatureItem[] = [];
  // sui-combobox options derived from `groups` ({ title, id } → { label, value }),
  // rebuilt whenever the groups list changes.
  groupOptions: ComboboxOption[] = [];

  searchString: string = '';
  selectedPresetGroupId: number = 0;
  selectedPresetId: number = null;
  selectedChartTypeId: number = null;

  selectedEvent: WidgetPreviewEvent = null;

  get widget(): Widget {
    return this.dialogData.widget;
  }

  ngOnInit(): void {
    this.widgetPresets$ = this.store.select(selectWidgetPresets);
    this.widgetPresetsLoading$ = this.store.select(selectLoadingWidgetPresets);

    this.store.dispatch(
      loadWidgetPresets({
        objectTypeId: this.dialogData.objectTypeId,
        objectClassId: this.dialogData.objectClassId,
      }),
    );

    this.store.dispatch(loadWidgetPresetGroups());

    this.store
      .select(selectWidgtetPresetGroups)
      .pipe(
        tap((groups) => {
          this.groups = [
            {
              id: 0,
              title: this.translateService.instant(
                'swap-widget-dialog.label.all-widgets',
              ),
            },
            ...groups,
          ];
          this.groupOptions = this.groups.map((group) => ({
            label: group.title,
            value: group.id,
          }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.widgetPresets$
      .pipe(
        tap((presets) => {
          this.widgetPresets = presets;
          this.widgetPresetsFiltered = presets;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleInputChange(searchString: string): void {
    this.searchString = searchString;
    this.filterWidgets(searchString);
  }

  filterWidgets(searchString: string): void {
    const search = searchString.toLowerCase();

    this.widgetPresetsFiltered = this.widgetPresets.filter((preset) => {
      const matchesSearch =
        search.length === 0 ||
        preset.name?.toLowerCase().includes(search) ||
        preset.description?.toLowerCase().includes(search);

      const matchesGroup =
        this.selectedPresetGroupId === 0 ||
        preset.group_id === this.selectedPresetGroupId;

      return matchesSearch && matchesGroup;
    });
  }

  onGroupChange(groupId: number): void {
    this.selectedPresetGroupId = groupId;
    this.filterWidgets(this.searchString);
  }

  onWidgetPreview(event: WidgetPreviewEvent): void {
    this.selectedPresetId = event.widgetPreset.id;
    this.selectedChartTypeId = event.vizType.id;
    this.selectedEvent = event;
  }

  confirm(): void {
    if (!this.selectedEvent) return;

    this.close({
      reason: DialogCloseReason.Confirm,
      data: {
        chartId: this.selectedEvent.widgetPreset.id,
        chartTypeId: this.selectedEvent.vizType.id,
      },
    });
  }

  cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }
}
